(() => {
  const sizingRootSelector = '[data-alpha-sizing-managed="true"]';
  const linkGroupSelector = '[data-alpha-size-links]';
  const triggerSelectors = {
    fit: [
      '.ks-container-app-block .ks-calculator-modal-link',
      '.ks-container-app-block .ks-product-rec-link',
    ],
    chart: [
      '.ks-container-app-block .ks-chart-modal-link',
      '.ks-container-app-block .ks-calculator-modal-link',
      '.ks-container-app-block .ks-product-rec-link',
    ],
  };
  const nativeTriggerSelector = Object.values(triggerSelectors).flat().join(',');
  const previewFlag = 'alpha_kiwi_sync_test';
  const nativeViewWaitAttempts = 150;
  const nativeViewWaitIntervalMs = 100;
  const sizingRetryMessage = 'Sizing tools are taking longer than expected. Please try again.';
  const preferredKiwiSizingByProductId = Object.freeze({
    '9051129512085': '4335479',
    '9051129577621': '4335324',
    '9262232830101': '4590972',
    '9288941306005': '4526629',
    '9288941371541': '4526791',
    '9288941731989': '4527483',
    '9288989376661': '4526691',
  });

  function resolvePreferredKiwiSizing({ calculatorIds = [], productId = '' }) {
    const normalizedIds = calculatorIds.map(String);
    const preferredId = preferredKiwiSizingByProductId[String(productId)] || '';
    if (!preferredId) return { status: 'unmanaged' };
    if (normalizedIds.length === 0) return { status: 'pending', preferredId };

    const preferredIndexes = normalizedIds
      .map((calculatorId, index) => (calculatorId === preferredId ? index : -1))
      .filter((index) => index >= 0);
    if (preferredIndexes.length !== 1) return { status: 'blocked', preferredId };
    if (normalizedIds.length === 1) {
      return { status: 'single', preferredId, preferredIndex: preferredIndexes[0] };
    }
    return {
      status: 'normalize',
      preferredId,
      preferredIndex: preferredIndexes[0],
      removeIndexes: normalizedIds
        .map((_, index) => index)
        .filter((index) => index !== preferredIndexes[0]),
    };
  }

  function sizingActionIsDeclared({ candidate, preview, supported }) {
    return supported === true || (candidate === true && preview === true);
  }

  function nativeActionForSizingAction(action) {
    return action;
  }

  function resolveSizingActions({
    nativeActions = [],
    preview = false,
    runtimeActions = [],
    sizeCount = 0,
    token = '',
  }) {
    const fitLauncherPresent = nativeActions.includes('fit');
    const chartLauncherPresent = nativeActions.includes('chart') || fitLauncherPresent;
    const chartDeclared = sizingActionIsDeclared({
      candidate: token.includes('|chart-candidate|'),
      preview,
      supported: token.includes('|chart|'),
    });
    const fitDeclared = sizeCount > 1 && sizingActionIsDeclared({
      candidate: token.includes('|fit-candidate|'),
      preview,
      supported: token.includes('|fit|'),
    });
    if (!preview) {
      return [
        fitDeclared && fitLauncherPresent && 'fit',
        chartDeclared && chartLauncherPresent && 'chart',
      ].filter(Boolean);
    }

    return [
      fitDeclared && runtimeActions.includes('fit') && fitLauncherPresent && 'fit',
      chartDeclared && runtimeActions.includes('chart') && chartLauncherPresent && 'chart',
    ].filter(Boolean);
  }

  globalThis.AlphaPdpSizing = Object.freeze({
    nativeActionForSizingAction,
    preferredKiwiSizingByProductId,
    resolveSizingActions,
    resolvePreferredKiwiSizing,
    sizingActionIsDeclared,
  });

  if (typeof document === 'undefined') return;

  function previewSizingTestEnabled() {
    return new URLSearchParams(window.location.search).get(previewFlag) === '1'
      && document.querySelector('#PBarNextFrame[src*="shopifycloud/preview-bar"]');
  }

  function findNativeTrigger(root, action) {
    for (const selector of triggerSelectors[action] || []) {
      const target = root.querySelector(selector);
      if (target instanceof HTMLElement) return target;
    }
    return null;
  }

  function setNativeTriggerGuarded(trigger) {
    if (trigger.dataset.alphaPdpNativeStateSaved !== 'true') {
      trigger.dataset.alphaPdpNativeStateSaved = 'true';
      trigger.dataset.alphaPdpHadAriaHidden = String(trigger.hasAttribute('aria-hidden'));
      trigger.dataset.alphaPdpOriginalAriaHidden = trigger.getAttribute('aria-hidden') || '';
      trigger.dataset.alphaPdpHadTabindex = String(trigger.hasAttribute('tabindex'));
      trigger.dataset.alphaPdpOriginalTabindex = trigger.getAttribute('tabindex') || '';
    }

    trigger.dataset.alphaPdpNativeGuarded = 'true';
    trigger.classList.add('alpha-pdp__kiwi-trigger-source');
    trigger.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('tabindex', '-1');
  }

  function releaseNativeTrigger(trigger) {
    trigger.classList.remove('alpha-pdp__kiwi-trigger-source');
    delete trigger.dataset.alphaPdpNativeGuarded;

    if (trigger.dataset.alphaPdpNativeStateSaved !== 'true') return;

    if (trigger.dataset.alphaPdpHadAriaHidden === 'true') {
      trigger.setAttribute('aria-hidden', trigger.dataset.alphaPdpOriginalAriaHidden || 'true');
    } else {
      trigger.removeAttribute('aria-hidden');
    }

    if (trigger.dataset.alphaPdpHadTabindex === 'true') {
      trigger.setAttribute('tabindex', trigger.dataset.alphaPdpOriginalTabindex || '0');
    } else {
      trigger.removeAttribute('tabindex');
    }
  }

  function hideNativeTriggers(root) {
    if (root.dataset.alphaSizingManaged !== 'true') return;
    root.querySelectorAll(nativeTriggerSelector).forEach(setNativeTriggerGuarded);
  }

  function showNativeTriggers(root) {
    root.querySelectorAll(nativeTriggerSelector).forEach(releaseNativeTrigger);
  }

  function nativeActionsForRoot(root) {
    return ['fit', 'chart'].filter((action) => findNativeTrigger(root, action));
  }

  function runtimeActionsForGroup(group) {
    return [...group.querySelectorAll('[data-alpha-sizing-action]')]
      .filter((button) => button.dataset.alphaSizingRuntimeVerified === 'true')
      .map((button) => button.dataset.alphaSizingAction);
  }

  function reconcileSizeLinkAvailability(root, group) {
    const buttons = [...group.querySelectorAll('[data-alpha-sizing-action]')];
    const availableActions = resolveSizingActions({
      nativeActions: nativeActionsForRoot(root),
      preview: Boolean(previewSizingTestEnabled()),
      runtimeActions: runtimeActionsForGroup(group),
      sizeCount: Number(group.dataset.alphaSizeValueCount || 0),
      token: group.dataset.alphaSizingCapabilities || '',
    });

    for (const button of buttons) {
      const available = availableActions.includes(button.dataset.alphaSizingAction)
        && button.dataset.alphaSizingRuntimeBlocked !== 'true';
      button.hidden = !available;
      button.disabled = !available;
      if (available) button.removeAttribute('aria-disabled');
      else button.setAttribute('aria-disabled', 'true');
    }

    const fitVisible = buttons.some(
      (button) => button.dataset.alphaSizingAction === 'fit' && !button.hidden,
    );
    const chartVisible = buttons.some(
      (button) => button.dataset.alphaSizingAction === 'chart' && !button.hidden,
    );
    const divider = group.querySelector('.alpha-pdp__size-link-divider');
    if (divider) divider.hidden = !(fitVisible && chartVisible);
    group.hidden = !(fitVisible || chartVisible);
    if (fitVisible || chartVisible) hideNativeTriggers(root);
    else showNativeTriggers(root);
  }

  function extractRecommendationText(value) {
    const normalizedValue = value?.replace(/\s+/g, ' ').trim() || '';
    const match = normalizedValue.match(/(?:recommended size|your size)\s*:\s*(.+)$/i);
    return match?.[1] ? `Recommended Size: ${match[1].trim()}` : '';
  }

  function extractRecommendationSize(value) {
    return extractRecommendationText(value).replace(/^Recommended Size:\s*/i, '').trim();
  }

  function findRecommendationText(root) {
    const triggers = [
      ...root.querySelectorAll(nativeTriggerSelector),
      ...(root === document ? [] : document.querySelectorAll(nativeTriggerSelector)),
    ];
    for (const trigger of new Set(triggers)) {
      const recommendation = extractRecommendationText(trigger.textContent);
      if (recommendation) return recommendation;
    }
    return '';
  }

  function syncFitRecommendation(root, group) {
    const fitButton = group.querySelector('[data-alpha-sizing-action="fit"]');
    const fitLabel = fitButton?.querySelector('[data-alpha-size-label]');
    if (!(fitButton instanceof HTMLButtonElement) || !(fitLabel instanceof HTMLElement)) return;

    const nextLabel = findRecommendationText(root)
      || fitButton.dataset.defaultLabel
      || 'Find Your Size';
    if (fitLabel.textContent.trim() !== nextLabel) fitLabel.textContent = nextLabel;

    const recommendedSize = extractRecommendationSize(nextLabel);
    const previousSize = group.dataset.alphaRecommendedSize || '';
    if (recommendedSize && recommendedSize !== previousSize) {
      group.dataset.alphaRecommendedSize = recommendedSize;
      document.dispatchEvent(new CustomEvent('alpha:kiwi-recommendation-change', {
        detail: { label: nextLabel, rawSize: recommendedSize, source: 'kiwi' },
      }));
    } else if (!recommendedSize && previousSize) {
      delete group.dataset.alphaRecommendedSize;
      document.dispatchEvent(new CustomEvent('alpha:kiwi-recommendation-change', {
        detail: { label: '', rawSize: '', source: 'kiwi' },
      }));
    }
  }

  function selectNativeSizingView(action, attempt = 0, onUnavailable = () => {}) {
    const label = action === 'chart' ? 'Size Chart' : 'Size Chart Calculator';
    const directViewSelector = action === 'chart'
      ? '.kiwi-sizing-modal .ks-table'
      : '.ks-calculator-tab-container .ks-calculator-container-content:not(.advanced-calculator)';
    const target = [...document.querySelectorAll('.ks-modal-tab')]
      .find((tab) => tab.textContent.trim() === label);

    if (target instanceof HTMLElement) {
      if (!target.classList.contains('selected')) target.click();
      return;
    }
    if (document.querySelector(directViewSelector)) return;
    if (attempt < nativeViewWaitAttempts) {
      window.setTimeout(
        () => selectNativeSizingView(action, attempt + 1, onUnavailable),
        nativeViewWaitIntervalMs,
      );
    } else {
      onUnavailable();
    }
  }

  function normalizeKnownKiwiOverlap(root) {
    const productId = String(root.dataset.productId || '');
    const preferredId = preferredKiwiSizingByProductId[productId];
    if (!preferredId) return { ready: true, status: 'unmanaged' };

    const modal = [...document.querySelectorAll('.kiwi-sizing-modal')]
      .find((candidate) => candidate.getClientRects().length > 0);
    if (!modal) return { ready: false, status: 'pending' };

    const calculators = [...modal.querySelectorAll(
      '.ks-calculator-container-content[data-calculator-id]',
    )];
    const chartOnlyTable = modal.querySelector('.ks-chart-tab-container .ks-table');
    if (calculators.length === 0 && chartOnlyTable) {
      modal.dataset.alphaKiwiPreferredSizingId = preferredId;
      modal.dataset.alphaKiwiOverlapState = 'chart-only';
      return { ready: true, status: 'chart-only' };
    }
    const resolution = resolvePreferredKiwiSizing({
      calculatorIds: calculators.map((calculator) => calculator.dataset.calculatorId || ''),
      productId,
    });
    if (resolution.status === 'pending') return { ready: false, status: 'pending' };
    if (resolution.status === 'blocked') return { ready: false, status: 'blocked' };
    if (resolution.status !== 'normalize') {
      modal.dataset.alphaKiwiPreferredSizingId = preferredId;
      modal.dataset.alphaKiwiOverlapState = 'single';
      return { ready: true, status: resolution.status };
    }

    const chartPanels = [...modal.querySelectorAll(
      '.ks-chart-tab-container .ks-chart-individual',
    )];
    if (chartPanels.length !== calculators.length) return { ready: false, status: 'pending' };

    const removedIds = [];
    for (const index of [...resolution.removeIndexes].sort((left, right) => right - left)) {
      const calculator = calculators[index];
      const calculatorWrapper = calculator?.parentElement;
      removedIds.push(calculator?.dataset.calculatorId || '');
      calculator?.remove();
      if (calculatorWrapper && calculatorWrapper.children.length === 0) {
        calculatorWrapper.remove();
      }
      chartPanels[index]?.remove();
    }
    modal.dataset.alphaKiwiPreferredSizingId = preferredId;
    modal.dataset.alphaKiwiRemovedSizingIds = removedIds.filter(Boolean).sort().join(',');
    modal.dataset.alphaKiwiOverlapState = 'normalized';
    return { ready: true, status: 'normalized' };
  }

  function selectPreferredNativeSizingView(
    root,
    action,
    attempt = 0,
    onUnavailable = () => {},
  ) {
    const normalization = normalizeKnownKiwiOverlap(root);
    if (normalization.ready) {
      selectNativeSizingView(action, 0, onUnavailable);
      return;
    }
    if (normalization.status === 'pending' && attempt < nativeViewWaitAttempts) {
      window.setTimeout(
        () => selectPreferredNativeSizingView(root, action, attempt + 1, onUnavailable),
        nativeViewWaitIntervalMs,
      );
      return;
    }
    onUnavailable();
  }

  function initializeSizeLinks(group) {
    if (group.dataset.alphaSizeLinksReady === 'true') return;

    const root = group.closest(sizingRootSelector) || document;
    const status = group.querySelector('[data-alpha-size-status]');
    const buttons = group.querySelectorAll('[data-alpha-sizing-action]');
    const setStatus = (message) => { if (status) status.textContent = message; };

    const observer = new MutationObserver(() => {
      reconcileSizeLinkAvailability(root, group);
      syncFitRecommendation(root, group);
    });
    observer.observe(root, { childList: true, characterData: true, subtree: true });
    reconcileSizeLinkAvailability(root, group);
    syncFitRecommendation(root, group);

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const action = button.dataset.alphaSizingAction;
        const nativeAction = nativeActionForSizingAction(action);
        let attempts = 0;
        button.setAttribute('aria-busy', 'true');
        setStatus(
          'Loading sizing tools…',
        );

        const openNativeSizing = () => {
          const nativeTrigger = findNativeTrigger(root, nativeAction);
          if (nativeTrigger) {
            hideNativeTriggers(root);
            nativeTrigger.dataset.alphaPdpProgrammaticClick = 'true';
            try {
              nativeTrigger.click();
            } finally {
              delete nativeTrigger.dataset.alphaPdpProgrammaticClick;
            }
            selectPreferredNativeSizingView(root, nativeAction, 0, () => {
              document.querySelector('.kiwi-sizing-modal .ks-modal-close')?.click();
              button.removeAttribute('aria-busy');
              setStatus(sizingRetryMessage);
            });
            syncFitRecommendation(root, group);
            button.removeAttribute('aria-busy');
            setStatus('');
            return;
          }

          attempts += 1;
          if (attempts < 20) {
            window.setTimeout(openNativeSizing, 150);
            return;
          }
          button.removeAttribute('aria-busy');
          setStatus(sizingRetryMessage);
        };
        openNativeSizing();
      });
    });
    group.dataset.alphaSizeLinksReady = 'true';
  }

  function initialize(root = document) {
    root.querySelectorAll?.(linkGroupSelector).forEach(initializeSizeLinks);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.(nativeTriggerSelector);
    const sizingRoot = trigger?.closest?.(sizingRootSelector);
    if (
      !trigger
      || !sizingRoot
      || trigger.dataset.alphaPdpNativeGuarded !== 'true'
      || trigger.dataset.alphaPdpProgrammaticClick === 'true'
    ) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('alpha:kiwi-capability-validated', (event) => {
    const actions = Array.isArray(event.detail?.actions) ? event.detail.actions : [];
    const productId = String(event.detail?.productId || '');
    const sizingRoot = [...document.querySelectorAll(sizingRootSelector)]
      .find((candidate) => candidate.dataset.productId === productId);
    const group = sizingRoot?.querySelector(linkGroupSelector);
    if (!sizingRoot || !group || actions.length === 0) return;
    for (const action of actions) {
      group.querySelectorAll(
        `[data-alpha-sizing-action="${action}"]`,
      ).forEach((button) => {
        if (button.dataset.alphaSizingRuntimeBlocked !== 'true') {
          button.dataset.alphaSizingRuntimeVerified = 'true';
        }
      });
    }
    reconcileSizeLinkAvailability(sizingRoot, group);
  });

  document.addEventListener('alpha:kiwi-capability-failed', (event) => {
    const actions = Array.isArray(event.detail?.actions) ? event.detail.actions : [];
    const productId = String(event.detail?.productId || '');
    const sizingRoot = [...document.querySelectorAll(sizingRootSelector)]
      .find((candidate) => candidate.dataset.productId === productId);
    const group = sizingRoot?.querySelector(linkGroupSelector);
    if (!sizingRoot || !group || actions.length === 0) return;
    for (const action of actions) {
      group.querySelectorAll(
        `[data-alpha-sizing-action="${action}"]`,
      ).forEach((button) => {
        delete button.dataset.alphaSizingRuntimeVerified;
        button.dataset.alphaSizingRuntimeBlocked = 'true';
      });
    }
    group.dataset.alphaSizingFailureReason = event.detail?.reason || 'runtime-validation-failed';
    reconcileSizeLinkAvailability(sizingRoot, group);
    if (actions.includes('chart') || actions.includes('fit')) {
      document.querySelector('.kiwi-sizing-modal .ks-modal-close')?.click();
    }
  });

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
